import { useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { getFullName } from "../../../../utils/urlUtils";
import { formatAddress, getDepartmentNames, getDesignationName } from "./useTeachers";

const DEFAULT_EXPORT_COLUMNS = {
  name: true,
  teacherCode: true,
  email: true,
  phoneNumber: true,
  aadhaarCard: true,
  subjects: true,
  qualification: true,
  experience: true,
  designation: false,
  department: false,
  dateOfJoining: false,
  teacherType: false,
  contractType: false,
  address: false,
  bloodGroup: false,
  birthday: false,
};

const ALL_COLUMN_LABELS = {
  name: "Name", teacherCode: "Teacher Code", employeeCode: "Employee Code",
  email: "Email", phoneNumber: "Phone Number", aadhaarCard: "Aadhaar Card",
  subjects: "Subjects", qualification: "Qualification", experience: "Experience",
  designation: "Designation", department: "Department", dateOfJoining: "Date of Joining",
  teacherType: "Teacher Type", contractType: "Contract Type", address: "Address",
  bloodGroup: "Blood Group", birthday: "Birthday", specialization: "Specialization",
  additionalRoles: "Additional Roles", notes: "Notes", status: "Status",
};

function getTeacherFieldValue(teacher, key) {
  switch (key) {
    case "name": return getFullName(teacher);
    case "teacherCode":
    case "employeeCode": return teacher.employeeCode || teacher.teacherCode || "";
    case "subjects": return teacher.subjects ? teacher.subjects.map((s) => s.name).join("; ") : "";
    case "address": return formatAddress(teacher.address);
    case "birthday": return teacher.birthday ? new Date(teacher.birthday).toLocaleDateString() : "Not provided";
    case "department": return getDepartmentNames(teacher);
    case "designation": return getDesignationName(teacher.designation);
    case "dateOfJoining": return teacher.dateOfJoining ? new Date(teacher.dateOfJoining).toLocaleDateString() : "";
    default: return teacher[key] || "";
  }
}

export function useTeacherExport({ fetchAllTeachersForExport }) {
  const [exportColumns, setExportColumns] = useState(DEFAULT_EXPORT_COLUMNS);
  const [previewData, setPreviewData] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const getSelectedKeys = () => Object.keys(exportColumns).filter((k) => exportColumns[k]);

  const handleExportCSV = async () => {
    const teachers = await fetchAllTeachersForExport();
    if (!teachers.length) return;

    const keys = getSelectedKeys();
    const headers = keys.map((k) => ALL_COLUMN_LABELS[k] || k.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()));
    let csvContent = headers.join(",") + "\n";
    teachers.forEach((teacher) => {
      const row = keys.map((k) => `"${getTeacherFieldValue(teacher, k).toString().replace(/"/g, '""')}"`);
      csvContent += row.join(",") + "\n";
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `teachers_${new Date().toISOString().split("T")[0]}.csv`;
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportExcel = async (format = "xlsx") => {
    const teachers = await fetchAllTeachersForExport();
    if (!teachers.length) return;

    const keys = getSelectedKeys();
    const headers = keys.map((k) => k.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()));
    let csvContent = headers.join(",") + "\n";
    teachers.forEach((teacher) => {
      const row = keys.map((k) => `"${getTeacherFieldValue(teacher, k).toString().replace(/"/g, '""')}"`);
      csvContent += row.join(",") + "\n";
    });

    const ext = format === "xls" ? "xls" : "xlsx";
    const blob = new Blob([csvContent], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `teachers_${new Date().toISOString().split("T")[0]}.${ext}`;
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = async () => {
    const teachers = await fetchAllTeachersForExport();
    if (!teachers.length) return;
    try {
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text("Teachers Report", 14, 22);
      doc.setFontSize(11);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);

      const keys = getSelectedKeys();
      const headers = keys.map((k) => k.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()));
      const data = teachers.map((t) => keys.map((k) => getTeacherFieldValue(t, k)));

      autoTable(doc, {
        head: [headers],
        body: data,
        startY: 40,
        theme: "grid",
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [66, 139, 202], textColor: 255, fontStyle: "bold" },
      });

      doc.save(`teachers_${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  const handlePreviewExport = async () => {
    const teachers = await fetchAllTeachersForExport();
    if (!teachers.length) return;
    const keys = getSelectedKeys();
    const previewRows = teachers.slice(0, 5).map((teacher) => {
      const row = {};
      keys.forEach((k) => { row[k] = getTeacherFieldValue(teacher, k); });
      return row;
    });
    setPreviewData(previewRows);
    setIsPreviewOpen(true);
  };

  const toggleColumn = (column) => {
    setExportColumns((prev) => ({ ...prev, [column]: !prev[column] }));
  };

  return {
    exportColumns, setExportColumns,
    previewData, isPreviewOpen, setIsPreviewOpen,
    handleExportCSV, handleExportExcel, handleExportPDF, handlePreviewExport,
    toggleColumn,
  };
}
